import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X } from "lucide-react";

const CLIENT_TYPES = ["Individual", "Corporate", "Family Office", "Government Delegation"];
const inp = "w-full bg-white border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-3 py-2 rounded-sm text-sm text-[#1A1D24]";
const empty = () => ({
  name: "", client_type: "Individual", email: "", phone: "", address: "",
  passport_notes: "", preferred_airports: [], preferred_hotels: [], preferred_transport: "",
  food_preferences: "", security_requirements: "", special_notes: "",
});

export default function OpsClients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [airportsInput, setAirportsInput] = useState("");
  const [hotelsInput, setHotelsInput] = useState("");

  const load = () => api.get("/clients").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...empty(), id: null }); setAirportsInput(""); setHotelsInput(""); };
  const openEdit = (c) => {
    setEditing({ ...c });
    setAirportsInput((c.preferred_airports || []).join(", "));
    setHotelsInput((c.preferred_hotels || []).join(", "));
  };
  const close = () => setEditing(null);

  const save = async () => {
    const { id, ...rest } = editing;
    const payload = {
      ...rest,
      preferred_airports: airportsInput.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
      preferred_hotels: hotelsInput.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      if (id) await api.put(`/clients/${id}`, payload);
      else await api.post(`/clients`, payload);
      toast.success("Client saved");
      close();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x?.msg || "").filter(Boolean).join("; ") : (typeof d === "string" ? d : "Save failed");
      toast.error(msg);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client ${c.name}? This will not delete their user account.`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="as-overline">Clients</div>
            <h1 className="as-serif text-5xl font-light mt-2" data-testid="ops-clients-title">VIP Directory</h1>
          </div>
          <button onClick={openNew} className="as-btn-gold px-5 py-3 rounded-sm as-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2" data-testid="client-add-btn"><Plus size={13}/> Add Client</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="ops-clients-grid">
          {rows.map(c => (
            <div key={c.id} className="as-card p-6" data-testid={`client-row-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{c.client_type}</div>
                  <div className="as-serif text-2xl mt-1">{c.name}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(c)} className="p-1.5 text-[#756E64] hover:text-[#A47E2A] hover:bg-[#F1EDE4] rounded-sm" data-testid={`client-edit-${c.id}`}><Edit3 size={14}/></button>
                  {isAdmin && <button onClick={() => remove(c)} className="p-1.5 text-[#756E64] hover:text-red-600 hover:bg-red-50 rounded-sm" data-testid={`client-delete-${c.id}`}><Trash2 size={14}/></button>}
                </div>
              </div>
              <div className="as-divider my-4"/>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Row label="Email" value={c.email}/>
                <Row label="Phone" value={c.phone}/>
                <Row label="Preferred Airports" value={c.preferred_airports?.join(", ")}/>
                <Row label="Hotels" value={c.preferred_hotels?.slice(0, 2).join(", ")}/>
                <Row label="Food" value={c.food_preferences} full/>
                <Row label="Security" value={c.security_requirements} full/>
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={close} data-testid="client-modal">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-md border border-[#E5DFD2] w-full max-w-2xl shadow-xl">
              <div className="flex items-center justify-between p-5 border-b border-[#E5DFD2]">
                <div className="as-serif text-2xl">{editing.id ? "Edit Client" : "Add Client"}</div>
                <button onClick={close} className="text-[#756E64] hover:text-[#1A1D24]"><X size={18}/></button>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                <Field label="Name"><input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} className={inp} data-testid="client-name"/></Field>
                <Field label="Client Type">
                  <select value={editing.client_type} onChange={e => setEditing({...editing, client_type: e.target.value})} className={inp} data-testid="client-type">
                    {CLIENT_TYPES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Email"><input value={editing.email || ""} onChange={e => setEditing({...editing, email: e.target.value})} className={inp}/></Field>
                <Field label="Phone"><input value={editing.phone || ""} onChange={e => setEditing({...editing, phone: e.target.value})} className={inp}/></Field>
                <Field label="Address" full><input value={editing.address || ""} onChange={e => setEditing({...editing, address: e.target.value})} className={inp}/></Field>
                <Field label="Preferred Airports (ICAO)" full><input value={airportsInput} onChange={e => setAirportsInput(e.target.value)} className={inp} placeholder="KTEB, LFPB"/></Field>
                <Field label="Preferred Hotels" full><input value={hotelsInput} onChange={e => setHotelsInput(e.target.value)} className={inp} placeholder="The Peninsula Paris, Aman NYC"/></Field>
                <Field label="Preferred Transport"><input value={editing.preferred_transport || ""} onChange={e => setEditing({...editing, preferred_transport: e.target.value})} className={inp}/></Field>
                <Field label="Passport Notes"><input value={editing.passport_notes || ""} onChange={e => setEditing({...editing, passport_notes: e.target.value})} className={inp}/></Field>
                <Field label="Food Preferences" full><textarea rows="2" value={editing.food_preferences || ""} onChange={e => setEditing({...editing, food_preferences: e.target.value})} className={inp}/></Field>
                <Field label="Security Requirements" full><textarea rows="2" value={editing.security_requirements || ""} onChange={e => setEditing({...editing, security_requirements: e.target.value})} className={inp}/></Field>
                <Field label="Special Notes" full><textarea rows="2" value={editing.special_notes || ""} onChange={e => setEditing({...editing, special_notes: e.target.value})} className={inp}/></Field>
              </div>
              <div className="p-5 border-t border-[#E5DFD2] flex justify-end gap-3">
                <button onClick={close} className="as-btn-ghost px-4 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest">Cancel</button>
                <button onClick={save} className="as-btn-gold px-5 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest" data-testid="client-save">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
function Row({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{label}</div>
      <div className="text-[#1A1D24] mt-1">{value || "—"}</div>
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <label className={full ? "md:col-span-2" : ""}>
      <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
